import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatCard } from "@cat-digital-blocks/react/bedrock/components/Card";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatTabs } from "@cat-digital-blocks/react/bedrock/components/Tabs";
import { CatTab } from "@cat-digital-blocks/react/bedrock/components/Tab";
import { CatTable } from "@cat-digital-blocks/react/bedrock/components/Table";
import { CatTableObject } from "@cat-digital-blocks/react/bedrock/components/TableObject";
import { CatTableBody } from "@cat-digital-blocks/react/bedrock/components/TableBody";
import { CatTableCell } from "@cat-digital-blocks/react/bedrock/components/TableCell";
import { CatTableHeader } from "@cat-digital-blocks/react/bedrock/components/TableHeader";
import { CatTableHeaderCell } from "@cat-digital-blocks/react/bedrock/components/TableHeaderCell";
import { CatTableRow } from "@cat-digital-blocks/react/bedrock/components/TableRow";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatIconClose } from "@cat-digital-blocks/react/bedrock/components/Icons/Close";
import styles from "./DataSourceRegistration.module.scss";
import {
  useGetPalTypesQuery,
  useGetDataSourceMappingsQuery,
  useGetAttributeMappingsQuery,
  useDeleteDataSourceMappingMutation,
} from "@services/dcmApi";
import type { DataSourceMappingResponse } from "@/types/api";
import CreateMappingModal from "./CreateMappingModal";
import { AttributeRegistration } from "../AttributeRegistration";
import { RuleRegistration } from "../RuleRegistration";

interface DataSourceRegistrationProps {
  dealerCode: string;
  currentUserName?: string;
}

const DataSourceRegistration = ({
  currentUserName,
}: DataSourceRegistrationProps) => {
  const { t } = useTranslation();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data: palTypes = [] } = useGetPalTypesQuery();

  const {
    data: allMappings = [],
    isLoading,
    error,
    refetch: refetchMappings,
  } = useGetDataSourceMappingsQuery();

  // Check if there are any attribute mappings to enable Rule Registration tab
  const { data: allAttributes = [] } = useGetAttributeMappingsQuery({});
  const hasAttributes = allAttributes.length > 0;
  const hasDataSources = allMappings.length > 0;

  const [deleteMapping, { isLoading: isDeleting }] =
    useDeleteDataSourceMappingMutation();

  const handleTabChange = useCallback((event: CustomEvent) => {
    const newIndex = event.detail?.activeTabIndex;
    if (typeof newIndex === "number") {
      setSelectedTabIndex(newIndex);
    }
  }, []);

  const handleDeleteMapping = useCallback(
    async (mapping: DataSourceMappingResponse) => {
      if (
        !window.confirm(
          t("admin.dataSourceRegistration.confirmDelete", {
            dataSource: mapping.sourceName,
          }),
        )
      ) {
        return;
      }
      try {
        await deleteMapping({
          id: mapping.id,
          userName: currentUserName,
        }).unwrap();
        setNotification({
          type: "success",
          message: t("admin.dataSourceRegistration.deleteSuccess"),
        });
        setTimeout(() => setNotification(null), 5000);
      } catch {
        setNotification({
          type: "error",
          message: t("admin.dataSourceRegistration.deleteError"),
        });
      }
    },
    [deleteMapping, currentUserName, t],
  );

  const handleCreateSuccess = useCallback(() => {
    setIsCreateModalOpen(false);
    setNotification({
      type: "success",
      message: t("admin.dataSourceRegistration.createSuccess"),
    });
    refetchMappings();
    setTimeout(() => setNotification(null), 5000);
  }, [refetchMappings, t]);

  if (error) {
    return (
      <div className={styles.container}>
        <CatInlineNotification variant="error">
          {t("errors.loadingFailed")}
        </CatInlineNotification>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <CatCard>
          <div className={styles.loading}>{t("common.loading")}</div>
        </CatCard>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CatHeading variant="headline">
          {t("admin.dataSourceAttributeRegistration.title")}
        </CatHeading>
        <p className={styles.description}>
          {t("admin.dataSourceAttributeRegistration.description")}
        </p>
      </div>

      {notification && (
        <CatInlineNotification
          variant={notification.type === "success" ? "success" : "error"}
          className={styles.notification}
        >
          {notification.message}
        </CatInlineNotification>
      )}

      <CatCard className={styles.mainCard}>
        <CatTabs
          id="data-source-tabs"
          activeIndex={selectedTabIndex}
          onBlTabChange={handleTabChange}
        >
          <CatTab>{t("admin.dataSourceRegistration.title")}</CatTab>
          <CatTab disabled={!hasDataSources}>
            {t("admin.attributeRegistration.title")}
          </CatTab>
          <CatTab disabled={!hasAttributes}>
            {t("admin.ruleRegistration.title")}
          </CatTab>
        </CatTabs>

        {/* Data Source Registration Tab Content */}
        {selectedTabIndex === 0 && (
          <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
              <CatHeading variant="title-sm">
                {t("admin.dataSourceRegistration.title")}
              </CatHeading>
              <CatButton
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                {t("admin.dataSourceRegistration.createNewMapping")}
              </CatButton>
            </div>

            <CatTableObject minWidth="100%">
              <CatTable className={styles.mappingsTable}>
                <CatTableHeader isFrozen>
                  <CatTableRow isLoading={isLoading}>
                    <CatTableHeaderCell width="22%" isFrozen>
                      {t("admin.dataSourceRegistration.table.databaseName")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="22%" isFrozen>
                      {t("admin.dataSourceRegistration.table.schemaName")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="22%" isFrozen>
                      {t("admin.dataSourceRegistration.table.sourceName")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="22%" isFrozen>
                      {t("admin.dataSourceRegistration.table.palType")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="12%" isFrozen>
                      {t("admin.dataSourceRegistration.table.actions")}
                    </CatTableHeaderCell>
                  </CatTableRow>
                </CatTableHeader>
                <CatTableBody>
                  {isLoading ? (
                    // Show loading skeleton rows
                    [...Array(3)].map((_, index) => (
                      <CatTableRow key={`loading-${index}`} isLoading>
                        <CatTableCell>-</CatTableCell>
                        <CatTableCell>-</CatTableCell>
                        <CatTableCell>-</CatTableCell>
                        <CatTableCell>-</CatTableCell>
                        <CatTableCell>-</CatTableCell>
                      </CatTableRow>
                    ))
                  ) : allMappings.length === 0 ? (
                    <CatTableRow>
                      <CatTableCell colspan={5} className={styles.emptyRow}>
                        {t("admin.dataSourceRegistration.noMappings")}
                      </CatTableCell>
                    </CatTableRow>
                  ) : (
                    allMappings.map((mapping) => (
                      <CatTableRow key={mapping.id}>
                        <CatTableCell>
                          {mapping.databaseName || "-"}
                        </CatTableCell>
                        <CatTableCell>{mapping.schemaName || "-"}</CatTableCell>
                        <CatTableCell>{mapping.sourceName}</CatTableCell>
                        <CatTableCell>{mapping.palType}</CatTableCell>
                        <CatTableCell>
                          <CatButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMapping(mapping)}
                            disabled={isDeleting}
                          >
                            <CatIconClose size="sm" />
                          </CatButton>
                        </CatTableCell>
                      </CatTableRow>
                    ))
                  )}
                </CatTableBody>
              </CatTable>
            </CatTableObject>
          </div>
        )}

        {/* Attribute Registration Tab Content */}
        {selectedTabIndex === 1 && (
          <div className={styles.tabContent}>
            <AttributeRegistration currentUserName={currentUserName} />
          </div>
        )}

        {/* Rule Registration Tab Content */}
        {selectedTabIndex === 2 && (
          <div className={styles.tabContent}>
            <RuleRegistration currentUserName={currentUserName} />
          </div>
        )}
      </CatCard>

      {isCreateModalOpen && (
        <CreateMappingModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          palTypes={palTypes}
          userName={currentUserName}
        />
      )}
    </div>
  );
};

export default DataSourceRegistration;
